'use client'

import React from 'react'
import { styled } from '@mui/material/styles'
import { Box, Typography as MTypography, Link as MLink } from '@mui/material'
import { Content, Sticky, Header, Divider } from '@/components/ui'
import { LegalInformation } from '@/components/legalInformation'
import Grid from '@mui/material/Grid2'

const Typography = styled(MTypography)`
  font-weight: normal;
`

const Policy: React.FC<{ title?: string; children: React.ReactNode }> = ({ title, children }) => (
  <Grid>
    {title && <MTypography variant="h6">{title}</MTypography>}
    <Typography>{children}</Typography>
  </Grid>
)

const PrivacyPolicy: React.FC = () => {
  return (
    <>
      <Sticky stickTo="top">
        <Header title="Privacy Policy" />
      </Sticky>
      <Content sx={{ pb: 4, flexGrow: 1 }}>
        <Box sx={{ pb: 4 }}>
          <Divider light />
        </Box>
        <Grid container spacing={4} direction="column">
          <Policy>
            The Charleston Wrap website available at{' '}
            <MLink target="_blank" href="/" rel="noopener">
              registercw.com
            </MLink>{' '}
            (“Charleston Wrap”) are owned and operated by Jaxco Industries, Inc ("us" "we" or "our"). This Charleston
            Wrap privacy policy (the "Privacy Policy") is intended to inform you of our policies and practices regarding
            the collection, use and disclosure of any information you submit to us through Charleston Wrap. This
            includes "Personal Information," which is information about you that is personally identifiable such as your
            name, e-mail address, user ID number, and other non-public information that is associated with the
            foregoing, as well as "Anonymous Information," which is information that is not associated with or linked to
            your Personal Information and does not permit the identification of individual persons.
          </Policy>
          <Policy title="User Consent">
            By accessing or otherwise using Charleston Wrap, you agree to the terms and conditions of this Privacy
            Policy and the associated Terms of Service (set forth on{' '}
            <MLink target="_blank" href="/terms-of-service" rel="noopener">
              registercw.com/terms-of-service
            </MLink>
            ) you expressly consent to the processing of your Personal Information and Anonymous Information according
            to this Privacy Policy. Your Personal Information may be processed by us in the country where it was
            collected as well as other countries (including the United States) where laws regarding processing of
            Personal Information may be less stringent than the laws in your country.
          </Policy>
          <Policy title="Regarding Children">
            Children under the age of 13 are not permitted to use Charleston Wrap and we do not intentionally collect or
            maintain Personal Information from those who are under 13 years old. Protecting the privacy of children is
            very important to us. Thus, if we obtain actual knowledge that a user is under 13, we will take steps to
            remove that user's Personal Information from our databases. We recommend that children between the ages of
            13 and 18 obtain their parent's permission before submitting information over the internet. By using
            Charleston Wrap, you are representing that you are at least 18 years old, or that you are at least 13 years
            old and have your parents' permission to use Charleston Wrap.
          </Policy>
          <Grid>
            <Box sx={{ mt: -1, mb: -2 }}>
              <MTypography variant="h5">Collection and Use of Information</MTypography>
            </Box>
          </Grid>
          <Policy title="Personal Information">
            In general, we collect Personal Information that you submit to us voluntarily through Charleston Wrap. We
            also collect information that you submit in the process of creating or editing your account and user profile
            on Charleston Wrap. For example, our registration and login process requires you to provide us with your
            name and valid email address. When you personalize your profile and use the features of Charleston Wrap, we
            will collect any information you voluntarily provide. We collect information in the form of the content that
            you submit during your use of Charleston Wrap, such as photos, comments, ratings and other information you
            choose to submit. We may also collect information about you and your friends, from any social network you
            may have connected from, in order to provide you with a more personalized experience. For instance, we may
            collect your user ID or profile information that you have permitted to be displayed through Charleston Wrap
            in order to display you as a friend or in association with your profile and collections. When you order our
            products or services, you will need to submit your credit card or other payment information so that our
            service providers can process your payment for those products and services. If you choose to sign up to
            receive information about products or services that may be of interest to you, we will collect your email
            address and all related information. Additionally, we collect any information that you voluntarily enter,
            including Personal Information, into any postings, comments, or forums within the Charleston Wrap community.
          </Policy>
          <Policy title="Personal Information from Other Sources">
            We may receive Personal Information about you from other sources with which you have registered, companies
            who we have partnered with (collectively, "Partners") or other third parties. We may associate this
            information with the other Personal Information we have collected about you.
          </Policy>
          <Policy title="E-mail and E-mail Addresses">
            If you send an e-mail to us, or fill out our "Feedback" form through Charleston Wrap, we will collect your
            e-mail address and the full content of your e-mail, including attached files, and other information you
            provide. We may use and display your full name and email address when you send an email notification to a
            friend through Charleston Wrap or the social network from which you have connected to Charleston Wrap (such
            as in an invitation, or when sharing your content). Additionally, we use your email address to contact you
            on behalf of your friends (such as when someone sends you a personal message) or notifications from a social
            network or other website with whom you have registered to receive such notifications. We may use this e-mail
            address to contact you, for things such as notifications of limited edition shop sales and other related
            information. You may indicate your preference to stop receiving further promotional communications as
            further detailed below.
          </Policy>
          <Policy title="Information Collected Via Technology">
            As you use Charleston Wrap, certain information may also be passively collected and stored on our or our
            service providers' server logs, including your Internet protocol address, browser type, and operating
            system. We also use Cookies and navigational data like Uniform Resource Locators (URL) to gather information
            regarding the date and time of your visit and the solutions and information for which you searched and
            viewed, or on which of the advertisements displayed on Charleston Wrap you clicked. This type of information
            is collected to make Charleston Wrap and solutions more useful to you and to tailor the experience with
            Charleston Wrap to meet your special interests and needs.
            <br />
            <br />
            An "Internet protocol address" or "IP Address" is a number that is automatically assigned to your computer
            when you use the Internet. In some cases your IP Address stays the same from browser session to browser
            session; but if you use a consumer internet access provider, your IP Address probably varies from session to
            session. For example, we, or our service providers, may track your IP Address when you access Charleston
            Wrap to assist with ad targeting.
            <br />
            <br />
            "Cookies" are small pieces of information that a website sends to your computer's hard drive while you are
            viewing a website. We may use both session Cookies (which expire once you close your web browser) and
            persistent Cookies (which stay on your computer until you delete them) to provide you with a more personal
            and interactive experience with Charleston Wrap. Persistent Cookies can be removed by following your
            Internet browser help file directions. In order to use our services offered through Charleston Wrap, your
            web browser must accept Cookies. If you choose to disable Cookies, some aspects of Charleston Wrap may not
            work properly, and you will not be able to receive our services.
            <br />
            <br />
            We may also enable advertisers and ad servers to promote third-party products and/or services by placing
            advertisements on Charleston Wrap. These advertisers and ad servers may use Cookies and/or "Web Beacons"
            (which are usually small, transparent graphic images) in order to monitor information related to served
            advertisements. Clicking on such advertisements will direct you to the website of a third-party and the
            advertiser. This Privacy Policy does not cover the privacy practices of any advertisers or ad servers.
          </Policy>
          <Policy title="Use and Disclosure of Information">
            Except as otherwise stated in this Privacy Policy, we do not generally sell, trade, rent, or share the
            Personal Information that we collect with third parties, unless you ask or authorize us to do so.
            <br />
            <br />
            In general, Personal Information you submit to us is used by us to provide you access to Charleston Wrap, to
            improve Charleston Wrap, to better tailor the features, performance, and support of Charleston Wrap and to
            offer you additional information, opportunities, promotions and functionality from us, our partners or our
            advertisers at your request. Additionally, we do share your content preferences and other information with
            the social network from which you have connected to Charleston Wrap, along with those companies and persons
            you have asked us to share your information with.
            <br />
            <br />
            We may provide your Personal Information to third-party service providers who work on behalf of or with us
            to provide some of the services and features of Charleston Wrap and to help us communicate with you.
            Examples of such services include sending email, analyzing data, providing marketing assistance, processing
            payments (including credit card payments), and providing customer service. We require our third-party
            service providers to promise not to use such information except as necessary to provide the relevant
            services to us. We may share some or all of your Personal Information with our third-party partners, those
            with whom we have a co-branding or promotional relationship, or other third-parties about whom you are
            otherwise notified and do not request to opt out of such sharing. This Privacy Policy does not cover the use
            of your personally identifiable information by such third-parties. We do not maintain responsibility for the
            manner in which third parties, including, without limitation, social networks, other partners and
            advertisers, use or further disclose Personal Information collected from you in accordance with this Privacy
            Policy, after we have disclosed such information to those third parties. If you do not want us to use or
            disclose Personal Information collected about you in the manners identified in this Privacy Policy, you may
            not use Charleston Wrap.
            <br />
            <br />
            Although we currently do not have a parent company, any subsidiaries, joint ventures, or other companies
            under a common control, we may in the future, and we may share some or all of your Personal Information with
            these companies, in which case we will require them to honor this Privacy Policy.
            <br />
            <br />
            In the event we go through a business transition such as a merger, acquisition by another company, or sale
            of all or a portion of its assets, your Personal Information may be among the assets transferred. You
            acknowledge that such transfers may occur and are permitted by this Privacy Policy, and that any acquirer of
            ours or that acquirer's assets may continue to process your Personal Information as set forth in this
            Privacy Policy. We may disclose your Personal Information if we believe in good faith that such disclosure
            is necessary to (a) comply with relevant laws or to respond to subpoenas or warrants served on us; or (b) to
            protect and defend our rights or property, you, or third parties. You hereby consent to us sharing your
            Personal Information under the circumstances described herein.
          </Policy>
          <Policy title="The Ability of others to View your Information">
            Helping you to protect your information is a vital part of our mission. It is up to you to make sure you are
            comfortable with the information you choose to provide us and the information you choose to publish. You
            understand that when you use Charleston Wrap, certain information you post or provide through Charleston
            Wrap, such as your name, profile, comments, posts and ratings, may be shared with other users and posted on
            publicly available portions of Charleston Wrap, including without limitation, chatrooms, bulletin and
            message boards, along with other public forums. Please keep in mind that if you choose to disclose Personal
            Information when posting comments or other information or content through Charleston Wrap, this information
            may become publicly available and may be collected and used by others, including people outside the
            Charleston Wrap community. We will not have any obligations with respect to any information that you post to
            parts of Charleston Wrap available to others, and recommend that you use caution when giving out personal
            information to others in public forums online or otherwise. We also share the information you publish with
            other third parties.
          </Policy>
          <Policy title="Third Party Sites and Advertising">
            Charleston Wrap may contain links to other websites. Please be aware that we are not responsible for the
            privacy practices or the content of such other websites. We encourage our users to read the privacy
            statements of each and every website they visit. This Charleston Wrap applies solely to information
            collected by us through Charleston Wrap and does not apply to these third-party websites. The ability to
            access information of third- parties from Charleston Wrap, or links to other websites or locations, is for
            your convenience and does not signify our endorsement of such third-parties, their products, their services,
            other websites, locations or their content.
          </Policy>
          <Policy title="Your Choices Regarding Your Personal Information">
            We offer you choices regarding the collection, use, and sharing of your Personal Information. When you
            receive promotional communications from us, you may indicate a preference to stop receiving further
            promotional communications from us and you will have the opportunity to "opt-out" by following the
            unsubscribe instructions provided in the promotional e-mail you receive or by contacting us directly (please
            see contact information below).
            <br />
            <br />
            Despite your indicated email preferences, we may send you administrative emails regarding Charleston Wrap,
            including, for example, administrative confirmations, and notices of updates to our Privacy Policy if we
            choose to provide such notices to you in this manner.
            <br />
            <br />
            You may change any of your profile information by editing it in the profile settings page. You may request
            deletion of your Personal Information by contacting us at custserv@charlestonwrap.com, but please note that
            we may be required (by law or otherwise) to keep this information and not delete it (or to keep this
            information for a certain time, in which case we will comply with your deletion request, only after we have
            fulfilled such requirements). When we delete Personal Information, it will be deleted from the active
            database, but may remain in our archives.
          </Policy>
          <Policy title="Feedback">
            If you provide feedback to us, we may use and disclose such feedback for any purpose, provided we do not
            associate such feedback with your Personal Information. We will collect any information contained in such
            feedback and will treat the Personal Information in it in accordance with this Privacy Policy. You agree
            that any such comments and any email we receive becomes our property. We may use feedback for marketing
            purposes or to add to or modify our services without paying any royalties or other compensation to you.
          </Policy>
          <Policy title="Security">
            We are committed to protecting the security of your Personal Information. We use a variety of
            industry-standard security technologies and procedures to help protect your Personal Information from
            unauthorized access, use, or disclosure. Even though we have taken significant steps to protect your
            Personal Information, no company, including us, can fully eliminate security risks associated with Personal
            Information.
            <br />
            <br />
            Contact and Revisions If you have questions or concerns about our Privacy Policy, please contact us at:
            custserv@charlestonwrap.com. This Privacy Policy is subject to occasional revision at our discretion, and if
            we make any substantial changes in the way we use your Personal Information, we will post an alert on this
            page. If you object to any such changes, you must cease using Charleston Wrap. Continued use of Charleston
            Wrap following notice of any such changes shall indicate your acknowledgement of such changes and agreement
            to be bound by the terms and conditions of such changes.
          </Policy>
          <Grid>
            <Typography align="center">
              <LegalInformation />
            </Typography>
          </Grid>
        </Grid>
      </Content>
    </>
  )
}

export default PrivacyPolicy
